// The clinic portal: shell + dashboard + case list + case workspace.
// Deliberately narrow — a doctor sees progress in customer language, their own records, the shared
// chat and the requests they've raised. Never internal notes, production records or staff names.
import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  formatDate,
  formatDateTime,
  formatFileSize,
  WF_PRIORITY_LABELS,
  type CaseCompliance,
  type ProgressPhoto,
  type WfPortalCase,
  type WfPortalCaseView,
  type WfPortalDashboard,
} from '@alignova/shared';
import { useAuth } from '../../auth/AuthContext.tsx';
import { AlignovaLockup } from '../../ui/AlignovaLogo.tsx';
import { useLiveEvent } from '../../lib/useLive.ts';
import { ProformaTable } from '../workflow/WfProforma.tsx';
import { CaseChat } from '../../ui/CaseChat.tsx';
import { isModelFile } from '../../ui/modelFiles.ts';
// three.js is only needed once a doctor actually opens a model, so it is not in the portal bundle.
const StlViewer = lazy(() => import('../../ui/StlViewer.tsx').then((m) => ({ default: m.StlViewer })));
import { Button, EmptyState } from '../../ui/primitives.tsx';
import {
  SectionLabel,
  Skeleton,
  SkeletonList,
  TabBar,
  useScrollLock,
  type TabDef,
} from '../../ui/mobile.tsx';
import {
  IconAlert,
  IconArrowLeft,
  IconCases,
  IconChat,
  IconChevronRight,
  IconClose,
  IconCube,
  IconMenu,
  IconOverview,
  IconPlus,
  IconProfile,
  IconSignOut,
  IconUpload,
} from '../../ui/icons.tsx';
import { PortalSetupTab } from './PortalSetup.tsx';
import { OutstandingTile } from './PortalBilling.tsx';
import { portalApi, portalFileUrl, portalInlineUrl, uploadPortalRecord, isPortalArchiveOutcome } from './portalApi.ts';
import {
  ComplianceBadge,
  ComplianceStats,
  LowWearNotice,
  PhotoSeries,
  WearChart,
  WearStatus,
} from '../../ui/compliance.tsx';

const NAV = [
  { to: '/portal', label: 'Dashboard', end: true },
  { to: '/portal/chat', label: 'Chat with lab' },
  { to: '/portal/cases', label: 'My Cases' },
  { to: '/portal/submit', label: 'Submit a Case' },
  { to: '/portal/invoices', label: 'Invoices' },
  { to: '/portal/ledger', label: 'Statement' },
  { to: '/portal/pay', label: 'Pay Company' },
  { to: '/portal/assessment', label: 'Case Assessment' },
  { to: '/portal/book-scan', label: 'Book a Scan' },
  { to: '/portal/notices', label: 'Notice Board' },
  { to: '/portal/preferences', label: 'Preferences' },
  { to: '/portal/profile', label: 'Clinic Profile' },
];

/** The four places a doctor goes from a phone. Everything else is one tap away under "More". */
const PORTAL_TABS: TabDef[] = [
  { to: '/portal', end: true, label: 'Home', icon: IconOverview },
  { to: '/portal/cases', label: 'Cases', icon: IconCases },
  { to: '/portal/chat', label: 'Lab chat', icon: IconChat },
];

export function PortalLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false); // mobile drawer
  useScrollLock(open);

  // A doctor taps a nav item expecting the drawer to get out of the way.
  useEffect(() => setOpen(false), [location.pathname]);

  const link = ({ isActive }: { isActive: boolean }) =>
    `tap-row flex items-center rounded-md px-3 py-2.5 text-[15px] font-medium transition lg:py-2 lg:text-sm ${
      isActive ? 'bg-primary text-primary-fg' : 'text-ink hover:bg-surface-2'
    }`;

  const logo = <AlignovaLockup sub="Provider Portal" size="sm" />;

  return (
    <div className="min-h-full bg-bg">
      <a
        href="#portal-main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-toast focus:rounded-md focus:bg-primary focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-primary-fg"
      >
        Skip to content
      </a>

      {/* Mobile top bar. pt-safe keeps the row clear of the notch when the portal is installed to
          the home screen and runs without browser chrome. */}
      <header className="fixed inset-x-0 top-0 z-bar flex items-center gap-3 border-b border-line bg-surface px-3 pt-safe lg:hidden">
        <div className="flex h-14 flex-1 items-center gap-2">
          <button
            onClick={() => setOpen(true)}
            aria-label="Open menu"
            className="tap grid h-10 w-10 place-items-center rounded-lg text-ink hover:bg-surface-2"
          >
            <IconMenu />
          </button>
          {logo}
        </div>
      </header>

      {/* Backdrop for the mobile drawer */}
      {open && (
        <div
          className="fixed inset-0 z-overlay bg-navy/50 animate-fade lg:hidden"
          onClick={() => setOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar - persistent on desktop, slide-in drawer on mobile */}
      <aside
        className={`fixed inset-y-0 left-0 z-drawer flex w-[84vw] max-w-xs transform flex-col border-r border-line bg-surface pt-safe transition-[transform,visibility] duration-200 ease-out lg:visible lg:w-64 lg:translate-x-0 ${
          open ? 'visible translate-x-0 shadow-level-3' : 'invisible -translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between border-b border-line px-4 py-3">
          {logo}
          <button
            onClick={() => setOpen(false)}
            aria-label="Close menu"
            className="tap grid h-10 w-10 place-items-center rounded-lg text-muted hover:bg-surface-2 lg:hidden"
          >
            <IconClose size={20} />
          </button>
        </div>

        <nav className="flex-1 space-y-0.5 overflow-y-auto px-3 py-3">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} className={link} onClick={() => setOpen(false)}>
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hairline-t px-3 py-3 pb-safe">
          <div className="mb-2 flex items-center gap-2.5 px-1">
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-surface-2 text-muted">
              <IconProfile size={20} />
            </span>
            <div className="min-w-0 truncate text-sm font-medium text-ink" title={user?.displayName ?? ''}>
              {user?.displayName}
            </div>
          </div>
          <Button variant="ghost" block onClick={() => void logout().then(() => nav('/login'))}>
            <IconSignOut size={18} />
            Sign out
          </Button>
        </div>
      </aside>

      {/* Content */}
      <main id="portal-main" className="px-4 pb-bar pt-[calc(3.5rem_+_env(safe-area-inset-top))] lg:pb-8 lg:pl-64 lg:pt-4">
        <div className="mx-auto max-w-6xl py-4 lg:px-4">
          <Outlet />
        </div>
      </main>

      <TabBar
        tabs={[...PORTAL_TABS, { label: 'More', icon: IconProfile, onClick: () => setOpen(true) }]}
        action={{ label: 'Submit a case', icon: IconPlus, onClick: () => nav('/portal/submit') }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// Dashboard
// ---------------------------------------------------------------------------------------------

export function PortalHome() {
  const nav = useNavigate();
  const [d, setD] = useState<WfPortalDashboard | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const load = useCallback(() => {
    portalApi.dashboard().then(setD).catch((e) => setErr(e instanceof Error ? e.message : String(e)));
  }, []);
  useEffect(load, [load]);
  useLiveEvent('workflow.changed', load);
  useLiveEvent('workflow.message', load);

  if (err) return <Err>{err}</Err>;
  if (!d)
    return (
      <div className="space-y-4">
        <Skeleton className="h-24 w-full" />
        <SkeletonList rows={3} />
      </div>
    );
  const t = d.totals;
  // Two ranks, not eight identical squares: the count a doctor acts on is the one in flight, and
  // the rest are a strip they can glance across.
  const strip = [
    { label: 'Under review', v: t.underReview },
    { label: 'In design', v: t.inDesign },
    { label: 'In production', v: t.inProduction },
    { label: 'Ready', v: t.ready },
    { label: 'Dispatched', v: t.dispatched },
    { label: 'On hold', v: t.onHold },
    { label: 'All time', v: t.total },
  ];
  return (
    <div className="space-y-6">
      {/* Greeting. No card border on a phone - the page itself is the card, and a box around the
          first thing on screen just eats width. */}
      <section className="ambient relative -mx-4 -mt-4 overflow-hidden px-4 pb-5 pt-5 lg:mx-0 lg:mt-0 lg:rounded-xl lg:border lg:border-line lg:bg-surface lg:p-6 lg:shadow-level-1">
        <div className="text-[13px] font-medium text-muted">Welcome back</div>
        <h1 className="mt-0.5 text-display text-ink lg:text-2xl lg:font-bold">{d.doctorName ?? d.clinicName}</h1>
        <p className="mt-1 text-sm text-muted">
          {d.clinicName} · Group ID <span className="font-mono font-semibold text-ink">{d.doctorCode}</span>
        </p>
        <div className="mt-4 flex items-center gap-3">
          <Link
            to="/portal/submit"
            className="tap inline-flex flex-1 items-center justify-center gap-2 rounded-md bg-accent px-5 py-3 text-[15px] font-semibold text-accent-fg shadow-level-1 sm:flex-none"
          >
            <IconUpload size={18} />
            Submit a case
          </Link>
          <Link
            to="/portal/cases"
            className="tap hidden items-center gap-1.5 rounded-md border border-line px-4 py-3 text-[15px] font-medium text-ink sm:inline-flex"
          >
            View all cases
          </Link>
        </div>
      </section>

      {d.actionNeeded.length > 0 && (
        <section className="rounded-lg border border-status-warn-fg/25 bg-status-warn-bg/60 p-4">
          <div className="mb-2.5 flex items-start gap-2.5">
            <span className="mt-0.5 text-status-warn-fg">
              <IconAlert size={20} />
            </span>
            <div>
              <b className="text-status-warn-fg">Action needed</b>
              <div className="text-[13px] text-status-warn-fg/90">
                {d.actionNeeded.length} case{d.actionNeeded.length > 1 ? 's' : ''} waiting on your review or approval
              </div>
            </div>
          </div>
          <ul className="space-y-1.5">
            {d.actionNeeded.map((c) => (
              <li key={c.id}>
                <button
                  onClick={() => nav(`/portal/cases/${c.id}`)}
                  className="tap-row flex w-full items-center gap-3 rounded-md bg-surface px-3 py-3 text-left"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-semibold tracking-tight text-ink">{c.patientName}</span>
                    <span className="text-xs text-muted">{c.clinicStatus}</span>
                  </span>
                  <span className="text-muted">
                    <IconChevronRight size={18} />
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section>
        <SectionLabel right={<Link to="/portal/cases" className="text-[13px] font-medium text-accent">See all</Link>}>
          Your cases
        </SectionLabel>
        <button
          onClick={() => nav('/portal/cases')}
          className="tap-row flex w-full items-baseline gap-3 rounded-lg border border-line bg-surface px-4 py-4 text-left shadow-level-1"
        >
          <span className="tabnum text-[2.5rem] font-extrabold leading-none tracking-tight text-ink">{t.active}</span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-medium text-ink">cases in progress</span>
            <span className="text-xs text-muted">Tap to open the full list</span>
          </span>
          <span className="self-center text-muted">
            <IconChevronRight size={18} />
          </span>
        </button>
        <ul className="no-scrollbar snap-strip -mx-4 mt-2 flex gap-2 overflow-x-auto px-4 lg:mx-0 lg:flex-wrap lg:px-0">
          {strip.map((x) => (
            <li key={x.label} className="shrink-0">
              <button
                onClick={() => nav('/portal/cases')}
                className="tap flex min-w-[7.5rem] flex-col rounded-md border border-line bg-surface px-3.5 py-2.5 text-left"
              >
                <span className="tabnum text-lg font-semibold text-ink">{x.v}</span>
                <span className="text-xs text-muted">{x.label}</span>
              </button>
            </li>
          ))}
        </ul>
      </section>

      <section className="space-y-2">
        {/* Money sits with the case CTAs, not buried in a menu - an unpaid balance is the thing
            most likely to hold a case up. */}
        <OutstandingTile balance={d.balance} pending={d.pendingPayments} onClick={() => nav('/portal/ledger')} />
        <div className="grid gap-2 sm:grid-cols-2">
          <Cta to="/portal/pay" title="Pay company" sub="Send a payment with proof so we can credit it" />
          <Cta to="/portal/assessment" title="Case assessment" sub="Send a complaint and photos for the lab to assess" />
          <Cta to="/portal/book-scan" title="Book a scan" sub="Reserve an appointment slot for your patient" />
          <Cta to="/portal/preferences" title="Clinical preferences" sub="Set your default treatment preferences" />
        </div>
      </section>

      <section>
        <SectionLabel>Recent cases</SectionLabel>
        <CaseList cases={d.recent} empty="No cases yet - submit your first one." />
      </section>
    </div>
  );
}

function Cta({ to, title, sub }: { to: string; title: string; sub: string }) {
  return (
    <Link to={to} className="tap-row flex items-center gap-3 rounded-md border border-line bg-surface px-4 py-3.5">
      <div className="min-w-0 flex-1">
        <div className="font-medium text-ink">{title}</div>
        <div className="text-xs text-muted">{sub}</div>
      </div>
      <span className="shrink-0 text-muted">
        <IconChevronRight size={18} />
      </span>
    </Link>
  );
}

/** One label/value pair in a case header. Two columns on a phone, four on a desk. */
function Meta({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted">{label}</dt>
      <dd className="truncate text-sm text-ink">{value}</dd>
    </div>
  );
}

function Err({ children }: { children: React.ReactNode }) {
  return <div className="rounded-lg border border-status-urgent-fg/30 bg-status-urgent-bg px-4 py-3 text-sm text-status-urgent-fg">{children}</div>;
}

function CaseList({ cases, empty }: { cases: WfPortalCase[]; empty: string }) {
  if (!cases.length)
    return (
      <EmptyState
        icon={<IconCases size={22} />}
        title={empty}
        body="Cases you send appear here with their progress, files and chat."
        action={
          <Link
            to="/portal/submit"
            className="tap inline-flex items-center gap-2 rounded-md bg-accent px-4 py-2.5 text-sm font-semibold text-accent-fg"
          >
            <IconUpload size={18} />
            Submit a case
          </Link>
        }
      />
    );
  // Phone layout is a row, not a wrapping flex soup: name and status on the first line, the
  // metadata a doctor scans (case number, type, dates) underneath at a single size.
  return (
    <ul className="stagger space-y-2">
      {cases.map((c) => (
        <li key={c.id}>
          <Link
            to={`/portal/cases/${c.id}`}
            className="tap-row flex items-center gap-3 rounded-lg border border-line bg-surface p-4 shadow-level-1"
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="min-w-0 flex-1 truncate font-semibold tracking-tight text-ink">
                  {c.patientName}
                </span>
                {c.priority !== 'normal' && (
                  <span className="shrink-0 rounded bg-status-warn-bg px-1.5 py-0.5 text-[11px] font-bold uppercase text-status-warn-fg">
                    {WF_PRIORITY_LABELS[c.priority]}
                  </span>
                )}
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted">
                <span
                  className={`rounded-full px-2 py-0.5 font-medium ${
                    c.awaitingYourApproval
                      ? 'bg-status-warn-bg text-status-warn-fg'
                      : c.isCancelled
                        ? 'bg-status-urgent-bg text-status-urgent-fg'
                        : 'bg-surface-2 text-ink'
                  }`}
                >
                  {c.clinicStatus}
                </span>
                <span className="font-mono">{c.caseNumber}</span>
                {c.dueDate && (
                  <span className={c.isOverdue ? 'font-semibold text-status-urgent-fg' : ''}>
                    Due {formatDate(c.dueDate)}
                  </span>
                )}
              </div>
              <div className="mt-0.5 truncate text-xs text-muted">
                {c.caseTypes.join(', ')} · sent {formatDate(c.receivedAt)}
              </div>
            </div>
            <span className="shrink-0 text-muted">
              <IconChevronRight size={18} />
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------------------------------------
// My cases
// ---------------------------------------------------------------------------------------------

export function PortalCases() {
  const [items, setItems] = useState<WfPortalCase[] | null>(null);
  const [includeDone, setIncludeDone] = useState(false);
  const [search, setSearch] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const load = useCallback(() => {
    portalApi
      .cases({ includeTerminal: includeDone, search: search.trim() || undefined })
      .then((r) => setItems(r.items))
      .catch((e) => setErr(e instanceof Error ? e.message : String(e)));
  }, [includeDone, search]);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);
  useLiveEvent('workflow.changed', load);
  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-3">
        <h1 className="text-[1.6rem] font-bold leading-tight tracking-tight text-ink lg:text-xl">My cases</h1>
        <Link
          to="/portal/submit"
          className="tap hidden items-center gap-2 rounded-md bg-accent px-4 py-2.5 text-sm font-semibold text-accent-fg sm:inline-flex"
        >
          <IconUpload size={18} />
          Submit a case
        </Link>
      </div>
      {/* The search row sticks under the phone header so a long list stays filterable without a
          scroll back to the top. */}
      <div className="sticky top-[calc(3.5rem_+_env(safe-area-inset-top))] z-sticky -mx-4 space-y-2 bg-bg px-4 py-2 lg:static lg:mx-0 lg:bg-transparent lg:px-0">
        <input
          className="w-full rounded-md border border-line bg-surface px-3.5 py-2.5 text-sm lg:w-64"
          placeholder="Search patient or case no…"
          aria-label="Search your cases"
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <label className="flex w-fit items-center gap-2 text-sm text-muted">
          <input
            type="checkbox"
            className="h-4 w-4 accent-[var(--c-accent)]"
            checked={includeDone}
            onChange={(e) => setIncludeDone(e.target.checked)}
          />
          Include completed cases
        </label>
      </div>
      {err && <Err>{err}</Err>}
      {!items && !err && <SkeletonList rows={4} />}
      {items && <CaseList cases={items} empty={search ? 'No cases match that search.' : 'No cases yet'} />}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// Case workspace
// ---------------------------------------------------------------------------------------------

type Tab = 'status' | 'setup' | 'progress' | 'files' | 'chat' | 'requests';

export function PortalCaseDetail() {
  const { id = '' } = useParams();
  const [sp] = useSearchParams();
  const [v, setV] = useState<WfPortalCaseView | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('status');
  const load = useCallback(() => {
    portalApi.case(id).then(setV).catch((e) => setErr(e instanceof Error ? e.message : String(e)));
  }, [id]);
  useEffect(load, [load]);
  useLiveEvent<{ id?: string }>('workflow.changed', (p) => {
    if (!p?.id || p.id === id) load();
  });

  if (err) return <Err>{err}</Err>;
  if (!v)
    return (
      <div className="space-y-3">
        <Skeleton className="h-5 w-24" />
        <Skeleton className="h-28 w-full" />
        <SkeletonList rows={2} />
      </div>
    );
  const c = v.case;
  const currentIdx = v.milestones.findIndex((m) => m.key === c.milestone);
  return (
    <div className="space-y-4">
      {sp.get('submitted') === '1' && (
        <div className="rounded-xl border border-status-progress-fg/30 bg-status-progress-bg px-4 py-3 text-sm text-status-progress-fg">
          <b>Case submitted.</b> The lab has it as <span className="font-mono">{c.caseNumber}</span> and will start reviewing your records.
        </div>
      )}
      {/* Every screen needs a way back - on a phone this link is the only one. */}
      <Link to="/portal/cases" className="tap inline-flex items-center gap-1.5 text-sm font-medium text-accent">
        <IconArrowLeft size={17} />
        My cases
      </Link>

      <header className="rounded-lg border border-line bg-surface p-4 shadow-level-1 lg:p-5">
        <div className="flex items-start justify-between gap-3">
          <h1 className="min-w-0 flex-1 text-[1.5rem] font-bold leading-tight tracking-tight text-ink lg:text-xl">
            {c.patientName}
          </h1>
          <span
            className={`shrink-0 rounded-full px-3 py-1 text-xs font-semibold ${
              c.awaitingYourApproval ? 'bg-status-warn-bg text-status-warn-fg' : 'bg-primary text-primary-fg'
            }`}
          >
            {c.clinicStatus}
          </span>
        </div>
        <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-4">
          <Meta label="Case no." value={<span className="font-mono">{c.caseNumber}</span>} />
          <Meta label="Type" value={c.caseTypes.join(', ') || '-'} />
          <Meta label="Submitted" value={formatDateTime(c.receivedAt)} />
          {c.dueDate && (
            <Meta
              label="Due"
              value={<span className={c.isOverdue ? 'font-semibold text-status-urgent-fg' : ''}>{formatDate(c.dueDate)}</span>}
            />
          )}
        </dl>
        {c.trackingNumber && (
          <p className="mt-3 rounded-md bg-surface-2 px-3 py-2 text-sm text-ink">
            Dispatched via {c.courier ?? 'courier'} · tracking{' '}
            <span className="font-mono font-semibold">{c.trackingNumber}</span>
          </p>
        )}
      </header>

      {/* Pills rather than underlined tabs: they stay tappable and scroll sideways when a count
          pushes the row past the width of a phone. */}
      <nav className="no-scrollbar snap-strip -mx-4 flex gap-1.5 overflow-x-auto px-4 lg:mx-0 lg:px-0">
        {(
          [
            ['status', 'Status'],
            ['setup', '3D setup'],
            ['progress', 'Progress'],
            ['files', `Files (${v.files.length})`],
            ['chat', 'Chat'],
            ['requests', `Requests (${v.requests.length})`],
          ] as Array<[Tab, string]>
        ).map(([k, label]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            aria-current={tab === k ? 'page' : undefined}
            className={`tap shrink-0 rounded-full px-4 py-2 text-sm font-medium ${
              tab === k ? 'bg-primary text-primary-fg' : 'bg-surface-2 text-ink'
            }`}
          >
            {label}
          </button>
        ))}
      </nav>

      {tab === 'status' && (
        <div className="space-y-4">
          <section className="rounded-xl border border-line bg-surface p-5 shadow-level-1">
            <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted">Case progress</h2>
            <ol className="flex flex-wrap gap-1">
              {v.milestones.map((m, i) => {
                const done = i < currentIdx;
                const on = i === currentIdx;
                return (
                  <li key={m.key} className="flex items-center gap-1">
                    <span className={`rounded-full px-3 py-1 text-xs font-medium ${on ? 'bg-primary text-primary-fg' : done ? 'bg-status-progress-bg text-status-progress-fg' : 'bg-surface-2 text-muted'}`}>
                      {done ? '✓ ' : on ? '⏳ ' : ''}
                      {m.label}
                    </span>
                    {i < v.milestones.length - 1 && <span className="text-line">-</span>}
                  </li>
                );
              })}
            </ol>
            {c.isOnHold && <p className="mt-3 rounded bg-status-warn-bg/60 px-3 py-2 text-sm text-status-warn-fg">This case is on hold. The lab will be in touch - or message them in Chat.</p>}
            {c.isCancelled && <p className="mt-3 rounded bg-status-urgent-bg px-3 py-2 text-sm text-status-urgent-fg">This case was cancelled.</p>}
          </section>

          <ApprovalPanel view={v} onDone={load} />
          <SharedSetupPanel view={v} />
          {v.proforma && !c.awaitingYourApproval && (
            <section className="rounded-xl border border-line bg-surface p-5 shadow-level-1">
              <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">Case proforma</h2>
              <ProformaTable p={v.proforma} />
            </section>
          )}

          <section className="rounded-xl border border-line bg-surface p-5 shadow-level-1">
            <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted">Progress history</h2>
            <ol className="relative space-y-3 border-l border-line pl-4">
              {[...v.timeline].reverse().map((e) => (
                <li key={e.id}>
                  <span className="absolute -left-1 mt-1.5 h-2 w-2 rounded-full bg-accent" />
                  <div className="text-xs text-muted">{formatDateTime(e.at)}</div>
                  <div className="text-sm text-ink">{e.label}</div>
                  {e.detail && <div className="text-xs text-muted">{e.detail}</div>}
                </li>
              ))}
            </ol>
          </section>
        </div>
      )}

      {tab === 'setup' && <PortalSetupTab caseId={id} />}
      {tab === 'progress' && <ProgressTab caseId={id} />}
      {tab === 'files' && <FilesTab view={v} onChanged={load} />}
      {tab === 'chat' && <CaseChat caseId={id} side="clinic" load={portalApi.messages} send={portalApi.sendMessage} />}
      {tab === 'requests' && <RequestsTab view={v} onChanged={load} />}
    </div>
  );
}

/**
 * The setup the doctor already signed off, kept on the page afterwards.
 *
 * Approving used to make the video vanish: the whole approval panel was hidden the moment the case
 * left the doctor-approval stage, and the media lived inside it. A doctor has every reason to go
 * back to what they approved — to check a movement against the plan, to show the patient, to settle
 * a question weeks later — so it stays, minus the decision controls, which are done with.
 */
function SharedSetupPanel({ view }: { view: WfPortalCaseView }) {
  const media = view.approvalMedia;
  // While the case is still awaiting approval the media belongs to ApprovalPanel, not here.
  if (!media || view.case.awaitingYourApproval) return null;
  return (
    <section className="rounded-xl border border-line bg-surface p-5 shadow-level-1">
      <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">The setup you approved</h2>
      <ApprovalMediaView caseId={view.case.id} media={media} />
    </section>
  );
}

function ApprovalPanel({ view, onDone }: { view: WfPortalCaseView; onDone: () => void }) {
  const [comments, setComments] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const c = view.case;
  const media = view.approvalMedia;
  if (!c.awaitingYourApproval) return null;
  const blocked = view.approvalBlockedReason;
  return (
    <section className="rounded-xl border border-status-warn-fg/25 bg-status-warn-bg/60 p-5">
      <h2 className="text-sm font-semibold text-status-warn-fg">Your approval is needed</h2>
      <p className="mt-1 text-sm text-status-warn-fg">
        {blocked ??
          (media
            ? 'Watch the setup the lab shared, then approve to move this case into production.'
            : 'Review what the lab shared in the Files tab, then approve to move this case into production.')}
      </p>
      {media && <ApprovalMediaView caseId={c.id} media={media} />}
      {view.proforma && (
        <div className="mt-3 rounded-lg border border-status-warn-fg/25 bg-surface p-3">
          <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted">Case proforma - what you are approving</div>
          <ProformaTable p={view.proforma} />
        </div>
      )}
      {err && <div className="mt-2 text-sm text-status-urgent-fg">{err}</div>}
      <textarea
        className="mt-3 w-full rounded border border-status-warn-fg/30 bg-surface px-3 py-2 text-sm"
        rows={2}
        placeholder="Optional instructions to include with your approval"
        value={comments}
        onChange={(e) => setComments(e.target.value)}
      />
      <div className="mt-2 flex flex-wrap gap-2">
        <button
          disabled={busy || !!blocked}
          onClick={() => {
            setBusy(true);
            setErr(null);
            portalApi
              .approve(c.id, comments || null)
              .then(onDone)
              .catch((e) => setErr(e instanceof Error ? e.message : String(e)))
              .finally(() => setBusy(false));
          }}
          className="rounded-lg bg-status-progress-fg px-5 py-2 text-sm font-semibold text-white disabled:opacity-40"
        >
          {busy ? 'Approving…' : '✓ Approve'}
        </button>
        <RequestButton caseId={c.id} kind="change" label="Request modifications" onDone={onDone} />
      </div>
    </section>
  );
}

function RequestButton({ caseId, kind, label, onDone, danger }: { caseId: string; kind: 'change' | 'treatment' | 'cancel'; label: string; onDone: () => void; danger?: boolean }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  if (!open)
    return (
      <button onClick={() => setOpen(true)} className={`rounded-lg border px-4 py-2 text-sm font-medium ${danger ? 'border-status-urgent-fg/40 text-status-urgent-fg' : 'border-line text-ink'} hover:bg-surface-2`}>
        {label}
      </button>
    );
  return (
    <div className="w-full rounded-lg border border-line bg-surface p-3">
      <div className="mb-1 text-sm font-medium text-ink">{label}</div>
      {err && <div className="mb-1 text-xs text-status-urgent-fg">{err}</div>}
      <textarea className="w-full rounded border border-line bg-surface px-3 py-2 text-sm" rows={3} autoFocus value={text} onChange={(e) => setText(e.target.value)} placeholder="Describe what you need…" />
      <div className="mt-2 flex gap-2">
        <button onClick={() => setOpen(false)} className="rounded-lg border border-line px-3 py-1.5 text-sm">
          Cancel
        </button>
        <button
          disabled={busy || text.trim().length < 3}
          onClick={() => {
            setBusy(true);
            setErr(null);
            portalApi
              .raiseRequest(caseId, { kind, description: text.trim() })
              .then(() => {
                setOpen(false);
                setText('');
                onDone();
              })
              .catch((e) => setErr(e instanceof Error ? e.message : String(e)))
              .finally(() => setBusy(false));
          }}
          className="rounded-lg bg-accent px-4 py-1.5 text-sm font-semibold text-accent-fg disabled:opacity-40"
        >
          {busy ? 'Sending…' : 'Send request'}
        </button>
      </div>
    </div>
  );
}

function FilesTab({ view, onChanged }: { view: WfPortalCaseView; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // The model the doctor is currently looking at, if any.
  const [view3d, setView3d] = useState<{ id: string; name: string } | null>(null);
  const [queue, setQueue] = useState<Array<{ name: string; pct: number; done: boolean; note?: string }>>([]);
  const upload = async (list: FileList | null) => {
    if (!list?.length) return;
    setBusy(true);
    setErr(null);
    const items = Array.from(list);
    setQueue(items.map((f) => ({ name: f.name, pct: 0, done: false })));
    try {
      for (let i = 0; i < items.length; i++) {
        const file = items[i]!;
        const out = await uploadPortalRecord(view.case.id, file, undefined, (pct) => setQueue((q) => q.map((x, j) => (j === i ? { ...x, pct } : x))));
        const note = isPortalArchiveOutcome(out) ? `extracted ${out.extracted.length} file${out.extracted.length === 1 ? '' : 's'}${out.skipped.length ? `, skipped ${out.skipped.length}` : ''}` : undefined;
        setQueue((q) => q.map((x, j) => (j === i ? { ...x, pct: 100, done: true, note } : x)));
      }
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  const fromLab = view.files.filter((f) => f.side === 'lab');
  const fromClinic = view.files.filter((f) => f.side === 'clinic');
  const models = view.files.filter((f) => isModelFile(f.originalName));
  const Group = ({ title, files, note }: { title: string; files: typeof view.files; note?: string }) => (
    <section className="rounded-xl border border-line bg-surface p-5 shadow-level-1">
      <h2 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted">
        {title} ({files.length})
      </h2>
      {note && <p className="mb-2 text-xs text-muted">{note}</p>}
      {files.length ? (
        <ul className="space-y-1.5">
          {files.map((f) => (
            <li key={f.id} className="flex flex-wrap items-center gap-x-2 gap-y-1 py-1 text-sm">
              <span className="min-w-0 flex-1 truncate text-ink">{f.description ?? f.originalName}</span>
              <span className="tabnum text-xs text-muted">{formatFileSize(f.sizeBytes)}</span>
              <span className="text-xs text-muted">{formatDate(f.uploadedAt)}</span>
              {/* An STL is the thing a doctor most wants to actually look at, and the one they were
                  previously only able to download and then find nothing on their phone that opens
                  it. The viewer is the same one the lab uses. */}
              {isModelFile(f.originalName) && (
                <button
                  type="button"
                  onClick={() => setView3d({ id: f.id, name: f.originalName })}
                  className="tap rounded-md bg-accent px-2.5 py-1 text-xs font-semibold text-accent-fg"
                >
                  View in 3D
                </button>
              )}
              <a href={portalFileUrl(view.case.id, f.id)} className="tap text-xs font-medium text-accent hover:underline">
                Download
              </a>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted">Nothing here yet.</p>
      )}
    </section>
  );
  return (
    <div className="space-y-4">
      {err && <Err>{err}</Err>}

      {/* 3D models get their own row at the top. On a phone an STL in a list is a dead end - there
          is usually nothing on the device that opens one - so the thing to do with it is offered
          before the list rather than buried in it. */}
      {models.length > 0 && (
        <section className="rounded-xl border border-line bg-surface p-5 shadow-level-1">
          <h2 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted">
            3D models ({models.length})
          </h2>
          <p className="mb-3 text-xs text-muted">
            Open these here - nothing to install, and it works on a phone.
          </p>
          <div className="flex flex-wrap gap-2">
            {models.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setView3d({ id: f.id, name: f.originalName })}
                className="tap flex items-center gap-2 rounded-lg bg-surface-2 px-3 py-2 text-sm font-medium text-ink transition hover:bg-line"
              >
                <IconCube size={17} />
                <span className="max-w-[14rem] truncate">{f.description ?? f.originalName}</span>
                <span className="tabnum text-xs text-muted">{formatFileSize(f.sizeBytes)}</span>
              </button>
            ))}
          </div>
        </section>
      )}

      <Group title="Shared by the lab" files={fromLab} note="Simulations, videos and setups the lab has shared with you." />
      <Group title="Records you sent" files={fromClinic} />
      <section className="rounded-xl border border-dashed border-line bg-surface p-5 text-center">
        <input type="file" multiple disabled={busy} accept=".stl,.ply,.obj,.3mf,.dcm,.jpg,.jpeg,.png,.gif,.webp,.heic,.bmp,.tif,.tiff,.mp4,.mov,.webm,.pdf,.doc,.docx,.txt,.xml,.csv,.xlsx,.zip,.rar" className="text-sm" onChange={(e) => { void upload(e.target.files); e.target.value = ''; }} />
        <p className="mt-1 text-xs text-muted">{busy ? 'Uploading…' : 'Add more records at any time - STL, scans, photos, PDFs, or a .zip / .rar (extracted automatically).'}</p>
        {queue.length > 0 && (
          <ul className="mx-auto mt-3 max-w-md space-y-1.5 text-left">
            {queue.map((q, i) => (
              <li key={i} className="text-xs">
                <div className="flex items-center gap-2">
                  <span className="truncate text-ink">{/\.(zip|rar)$/i.test(q.name) ? '🗜 ' : ''}{q.name}</span>
                  <span className="ml-auto font-mono text-muted">{q.done ? <span className="font-bold text-status-progress-fg">✓</span> : `${q.pct}%`}</span>
                </div>
                <div className="mt-0.5 h-1 overflow-hidden rounded bg-surface-2"><div className={`h-full transition-all ${q.done ? 'bg-status-progress-fg' : 'bg-accent'}`} style={{ width: `${q.pct}%` }} /></div>
                {q.note && <div className="text-[11px] text-muted">{q.note}</div>}
              </li>
            ))}
          </ul>
        )}
      </section>

      {view3d && (
        <Suspense
          fallback={
            <div className="fixed inset-0 z-modal grid place-items-center bg-black/85 text-sm text-white/70">
              Loading the viewer…
            </div>
          }
        >
          <StlViewer
            url={portalInlineUrl(view.case.id, view3d.id)}
            name={view3d.name}
            onClose={() => setView3d(null)}
          />
        </Suspense>
      )}
    </div>
  );
}

function RequestsTab({ view, onChanged }: { view: WfPortalCaseView; onChanged: () => void }) {
  const STATUS: Record<string, string> = { open: 'bg-status-warn-bg text-status-warn-fg', accepted: 'bg-status-progress-bg text-status-progress-fg', declined: 'bg-status-urgent-bg text-status-urgent-fg', done: 'bg-surface-2 text-ink' };
  return (
    <div className="space-y-4">
      <section className="rounded-xl border border-line bg-surface p-5 shadow-level-1">
        <h2 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted">Ask the lab for a change</h2>
        <p className="mb-3 text-xs text-muted">Requests go to the lab to action - the case itself is only changed by them.</p>
        <div className="flex flex-wrap gap-2">
          <RequestButton caseId={view.case.id} kind="change" label="Request modifications" onDone={onChanged} />
          <RequestButton caseId={view.case.id} kind="treatment" label="Request treatment-plan change" onDone={onChanged} />
          <RequestButton caseId={view.case.id} kind="cancel" label="Request cancellation" onDone={onChanged} danger />
        </div>
      </section>
      <section className="rounded-xl border border-line bg-surface p-5 shadow-level-1">
        <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">Your requests</h2>
        {view.requests.length ? (
          <ul className="space-y-2">
            {view.requests.map((r) => (
              <li key={r.id} className="rounded-lg border border-line p-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium capitalize text-ink">{r.kind}</span>
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${STATUS[r.status] ?? ''}`}>{r.status}</span>
                  <span className="ml-auto text-xs text-muted">{formatDateTime(r.raisedAt)}</span>
                </div>
                <p className="mt-1 whitespace-pre-wrap text-sm text-ink">{r.description}</p>
                {r.resolution && <p className="mt-1 text-xs text-muted">Lab: {r.resolution}</p>}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted">No requests raised.</p>
        )}
      </section>
    </div>
  );
}

/**
 * The setup the lab shared for sign-off. A video plays in place — the doctor should never have to
 * download a 40MB file onto their phone to answer a yes/no question. The server serves it with
 * Range support, so scrubbing works.
 */
function ApprovalMediaView({ caseId, media }: { caseId: string; media: NonNullable<WfPortalCaseView['approvalMedia']> }) {
  return (
    <figure className="mt-3 overflow-hidden rounded-lg border border-line bg-surface">
      {media.isVideo ? (
        <video
          src={portalInlineUrl(caseId, media.fileId)}
          controls
          playsInline
          preload="metadata"
          className="block max-h-[60vh] w-full bg-navy"
        />
      ) : media.isImage ? (
        <img src={portalInlineUrl(caseId, media.fileId)} alt={media.originalName} className="block w-full" />
      ) : (
        <div className="p-4">
          <p className="text-sm text-ink">{media.originalName}</p>
          <a
            href={portalFileUrl(caseId, media.fileId)}
            className="tap mt-2 inline-flex rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-fg"
          >
            Open the file
          </a>
        </div>
      )}
      <figcaption className="px-3 py-2 text-xs text-muted">
        {media.note ? <span className="mb-0.5 block text-sm text-ink">{media.note}</span> : null}
        {media.originalName} · {formatFileSize(media.sizeBytes)}
        {media.sharedAt ? ` · shared ${formatDateTime(media.sharedAt)}` : ''}
      </figcaption>
    </figure>
  );
}

/**
 * Progress: how the patient is actually wearing their aligners, and the photos they have sent.
 * Read-only — the doctor sees exactly what the lab sees, from the same computation.
 */
function ProgressTab({ caseId }: { caseId: string }) {
  const [c, setC] = useState<CaseCompliance | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const load = useCallback(() => {
    portalApi
      .compliance(caseId)
      .then(setC)
      .catch((e) => setErr(e instanceof Error ? e.message : String(e)));
  }, [caseId]);
  useEffect(load, [load]);
  useLiveEvent<{ id?: string }>('workflow.changed', (p) => {
    if (!p?.id || p.id === caseId) load();
  });

  if (err) return <Err>{err}</Err>;
  if (!c) return <SkeletonList rows={2} />;

  const photoUrl = (p: ProgressPhoto) => portalInlineUrl(caseId, p.fileId);

  return (
    <div className="space-y-4">
      <section className="rounded-lg border border-line bg-surface p-4 shadow-level-1 lg:p-5">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold text-ink">Aligner wear</h2>
            {c.hasPatientApp && <ComplianceBadge adherence={c.adherence7} />}
          </div>
          <WearStatus c={c} />
        </div>

        {c.hasPatientApp ? (
          <>
            <LowWearNotice c={c} />
            <div className="mt-3">
              <WearChart days={c.days} targetHours={c.targetHours} />
            </div>
            <div className="mt-4">
              <ComplianceStats c={c} />
            </div>
            {c.trays.total ? (
              <p className="mt-3 text-sm text-muted">
                Tray <span className="tabnum font-semibold text-ink">{c.trays.current ?? 1}</span> of{' '}
                <span className="tabnum">{c.trays.total}</span>
                {c.trays.nextChangeDate ? ` · next change ${formatDate(c.trays.nextChangeDate)}` : ''}
              </p>
            ) : null}
          </>
        ) : (
          <p className="text-sm text-muted">
            Your patient hasn't been set up on the Alignova patient app yet. Ask the lab to issue a
            login and their wear record will appear here.
          </p>
        )}
      </section>

      <section className="rounded-lg border border-line bg-surface p-4 shadow-level-1 lg:p-5">
        <h2 className="mb-3 text-sm font-semibold text-ink">Progress photos</h2>
        <PhotoSeries
          photos={c.photos}
          urlFor={photoUrl}
          empty="Your patient hasn't sent any photos yet. The app asks for a set at every tray change."
        />
      </section>
    </div>
  );
}

export { Err as PortalError, CaseList as PortalCaseList };
export const useMemoNoop = useMemo;
